import { Typography, Box, Accordion, AccordionDetails, AccordionSummary, Button, Grid } from '@mui/material';
import parser from 'html-react-parser';
import { FC, ReactElement } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { AddOn, SelectedAddOn } from '@acorn-portal/shared/src/services/renewalsApi';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { OPTIONAL_EXTRAS_MONTHLY_COST_DISCLAIMER } from '@acorn-portal/shared/src/utils/constants';
import { getAdditionalExtraText } from '@acorn-portal/shared/src/utils/get-additional-extra-text';

interface IRenderDescriptionProps {
	description: string;
}

interface IProps {
	option: AddOn;
	onRemove: (underwrittenDataPointId: string) => void;
	onSelect: (selectedAddOns: SelectedAddOn[]) => void;
	disabled?: boolean;
	renderDescription?: (props: IRenderDescriptionProps) => ReactElement;
}

const OptionalExtra: FC<IProps> = ({
	option,
	onRemove,
	onSelect,
	disabled = false,
	renderDescription = ({ description }) => <Typography>{description}</Typography>,
}) => {
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const paymentInFull = renewalStore.paymentOptions.method === 'total';

	const onAdd = () => {
		onSelect([
			{
				underwrittenDataPointId: option.underwrittenDataPointId,
				addOnCode: option.addOnCode,
			},
		]);
	};

	return (
		<Box
			sx={(theme) => ({
				backgroundColor: '#fff',
				borderRadius: 2,
				pl: 2,
				pr: 2,
				mb: 3,
				'& .MuiOutlinedInput-root': {
					backgroundColor: theme.palette.background.paper,
				},
			})}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					my: 1,
				}}
			>
				<Box>
					{renderDescription({ description: option.addOnDescription })}
					{paymentInFull ? (
						<Typography variant='h5' fontWeight='bold'>
							{formatCurrency(option.annualCost)}
						</Typography>
					) : (
						<Grid>
							<Typography variant='h5' fontWeight='bold'>
								{formatCurrency(option.monthlyCost)}
								{' per month'}
							</Typography>
						</Grid>
					)}
				</Box>
				{option.selected && <CheckCircleIcon color='primary' fontSize='large' />}
			</Box>

			{option.selected ? (
				<Button
					aria-label={`Optional-Remove-${option.addOnDescription}`}
					fullWidth
					variant='contained'
					startIcon={<DeleteIcon />}
					onClick={() => {
						onRemove(option.underwrittenDataPointId);
					}}
					disabled={disabled}
				>
					Remove
				</Button>
			) : (
				<Button
					aria-label={`Optional-Add-${option.addOnDescription}`}
					fullWidth
					variant='contained'
					onClick={() => {
						onAdd();
					}}
					disabled={disabled}
				>
					Add
				</Button>
			)}
			<Typography variant='body2' sx={{ pt: 2, mb: '1rem', fontStyle: 'italic' }}>
				{OPTIONAL_EXTRAS_MONTHLY_COST_DISCLAIMER}
			</Typography>

			<Accordion
				sx={(theme) => ({
					backgroundColor: 'unset',
					borderRadius: 'unset',
					borderTopLeftRadius: 'unset',
					borderTopRightRadius: 'unset',
					mt: 2,
					boxShadow: 'unset',
					borderBottom: `1px solid ${theme.palette.grey[300]}`,

					'&:before': {
						backgroundColor: 'unset',
					},
					'&:last-of-type': {
						borderBottomLeftRadius: 'unset',
						borderBottomRightRadius: 'unset',
					},
				})}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='tell-me-more-content' id='tell-me-more-header'>
					<Typography>Tell me more</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Typography
						sx={(theme) => ({
							'& a': {
								color: theme.palette.primary.main,
								'&:hover': {
									color: theme.palette.primary.light,
								},
								'&:visited': {
									color: theme.palette.primary.dark,
									'&:hover': {
										color: theme.palette.primary.light,
									},
								},
							},
						})}
					>
						{parser(getAdditionalExtraText(option.addOnDescription, fullPolicy.scheme, renewalStore.renewalStartDate))}
					</Typography>
				</AccordionDetails>
			</Accordion>
		</Box>
	);
};

export default OptionalExtra;
